import { useState, useEffect, useMemo } from 'react';
import { useApp } from '../../contexts/AppContext';
import { useTenant } from '../../contexts/TenantContext';
import StatCard from '../ui/StatCard';
import { formatDateTime } from '../../lib/helpers';
import { StatusBadge } from '../ui/Badge';

export default function Dashboard({ setPage }) {
  const { assets, facilities, antennas, checkouts, requests, deliveries, movements } = useApp();
  const { features } = useTenant();
  const f = features || {};
  const { org, limits, usage } = useTenant();
  const [showPlanWidget, setShowPlanWidget] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { supabase } = await import('../../supabaseClient');
        const { data } = await supabase
          .from('platform_settings')
          .select('value')
          .eq('key', 'show_plan_widget')
          .maybeSingle();
        if (data) setShowPlanWidget(data.value !== 'false');
      } catch(e) {}
    };
    load();
  }, []);

  const stats = useMemo(() => ({
    totalAssets:    assets.length,
    inUse:          assets.filter(a => a.status === 'In Use').length,
    inTransit:      assets.filter(a => a.status === 'In Transit').length,
    totalFacilities: facilities.length,
    activeFacilities: facilities.filter(f => f.status === 'Active').length,
    onlineAntennas: antennas.filter(a => a.status === 'Online').length,
    offlineAntennas: antennas.filter(a => a.status === 'Offline').length,
    activeCheckouts: checkouts.filter(c => c.status === 'Out').length,
    pendingRequests: requests.filter(r => r.status === 'Pending').length,
    inTransitDels:  deliveries.filter(d => d.status === 'In Transit').length,
    recentScans:    movements.slice(0, 10),
  }), [assets, facilities, antennas, checkouts, requests, deliveries, movements]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Stat grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>
        <StatCard icon="📦" label="Total Assets"     value={stats.totalAssets}     sub={`${stats.inUse} in use`}                  accent="#5b9cf6" onClick={() => setPage('assets')} />
        <StatCard icon="🏭" label="Facilities"        value={stats.activeFacilities} sub={`${stats.totalFacilities} total`}          accent="#00e5a0" onClick={() => setPage('facilities')} />
        <StatCard icon="📡" label="Online Antennas"   value={stats.onlineAntennas}  sub={`${stats.offlineAntennas} offline`}         accent="#00e5a0" onClick={() => setPage('tracking')} />
        {f.can_checkout !== false && <StatCard icon="🔄" label="Active Checkouts"  value={stats.activeCheckouts} sub="assets currently out"                      accent="#b89de8" onClick={() => setPage('checkout')} />}
        {f.can_requests !== false && <StatCard icon="📋" label="Pending Requests"  value={stats.pendingRequests} sub="awaiting approval"                         accent="#f5a623" onClick={() => setPage('requests')} />}
        {f.can_deliveries !== false && <StatCard icon="🚚" label="In Transit"         value={stats.inTransitDels}  sub="active deliveries"                         accent="#5b9cf6" onClick={() => setPage('deliveries')} />}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Recent RFID scans */}
        <div className="card" style={{ gridColumn: '1 / -1' }}>
          <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 14, color: '#e8edf2', marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
            📡 Recent RFID Scans
            <button className="btn" style={{ fontSize: 10, padding: '3px 10px', background: 'none', border: '1px solid #1a2a3a', color: '#a0b4c0' }}
              onClick={() => setPage('tracking')}>View All</button>
          </div>
          {stats.recentScans.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px 0', color: '#7090a0', fontSize: 13 }}>
              No scans recorded yet
            </div>
          ) : <div style={{ maxHeight: 360, overflowY: 'auto' }}>{stats.recentScans.map((m, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: i < stats.recentScans.length - 1 ? '1px solid #1a2a3a' : 'none' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#00e5a0', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#c8d6e0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.asset_name}</div>
                <div style={{ fontSize: 10, color: '#7090a0' }}>{m.to_facility_name || m.facility_name || '—'} · {m.reader_name || m.antenna_location || ''}</div>
              </div>
              <div style={{ fontSize: 10, color: '#7090a0', fontFamily: "'IBM Plex Mono',monospace", flexShrink: 0 }}>
                {formatDateTime(m.detected_at)}
              </div>
            </div>
          ))}</div>}
        </div>

        {/* Pending requests */}
        {f.can_requests !== false && <div className="card">
          <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 14, color: '#e8edf2', marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
            📋 Pending Requests
            <button className="btn" style={{ fontSize: 10, padding: '3px 10px', background: 'none', border: '1px solid #1a2a3a', color: '#a0b4c0' }}
              onClick={() => setPage('requests')}>View All</button>
          </div>
          {requests.filter(r => r.status === 'Pending').slice(0, 5).length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px 0', color: '#7090a0', fontSize: 13 }}>
              No pending requests
            </div>
          ) : requests.filter(r => r.status === 'Pending').slice(0, 5).map((r, i, arr) => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: i < arr.length - 1 ? '1px solid #1a2a3a' : 'none' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#c8d6e0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.assetName}</div>
                <div style={{ fontSize: 10, color: '#7090a0' }}>{r.fromFacilityName} → {r.toFacilityName}</div>
              </div>
              <StatusBadge status={r.status} />
            </div>
          ))}
        </div>}
      </div>

      {/* ── Billing & Plan ── */}
      {limits && showPlanWidget && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
            <div>
              <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 14, color: '#e8edf2' }}>
                💳 Plan & Usage
              </div>
              <div style={{ fontSize: 11, color: '#7090a0', marginTop: 3 }}>
                {org?.name} · <span style={{ color: '#00e5a0', fontWeight: 700 }}>{limits.package_name || 'Starter'}</span> plan
              </div>
            </div>
            <span style={{
              background: '#00e5a018', border: '1px solid #00e5a030', color: '#00e5a0',
              borderRadius: 8, padding: '4px 14px', fontSize: 12, fontWeight: 700,
            }}>{limits.package_name || 'Starter'}</span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
            {[
              { label: 'Assets',     used: assets.length,                              max: limits.asset_limit,    icon: '📦' },
              { label: 'Users',      used: facilities.length,                           max: limits.user_limit,     icon: '👥' },
              { label: 'Facilities', used: facilities.length,                           max: limits.facility_limit, icon: '🏭' },
              { label: 'Antennas',   used: antennas.length,                             max: limits.antenna_limit,  icon: '📡' },
              ...(f.can_requests   !== false ? [{ label: 'Requests',   used: usage?.requests_count  || 0, max: limits.request_limit,  icon: '📋', sub: 'this month' }] : []),
              ...(f.can_deliveries !== false ? [{ label: 'Deliveries', used: usage?.deliveries_count || 0, max: limits.delivery_limit, icon: '🚚', sub: 'this month' }] : []),
            ].map(({ label, used, max, icon, sub }) => {
              const unlimited = max === -1;
              const pct       = unlimited ? 0 : Math.min((used / Math.max(max, 1)) * 100, 100);
              const danger    = pct >= 90;
              const warn      = pct >= 70;
              const barColor  = danger ? '#e05757' : warn ? '#f5a623' : '#00e5a0';
              return (
                <div key={label} style={{ background: '#060b10', border: '1px solid #1a2a3a', borderRadius: 10, padding: '12px 14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ fontSize: 11, color: '#7090a0', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                      {icon} {label}
                    </div>
                    <div style={{ fontSize: 11, color: unlimited ? '#00e5a0' : '#c8d6e0', fontFamily: "'IBM Plex Mono',monospace", fontWeight: 700 }}>
                      {used}{unlimited ? '' : ` / ${max}`}
                      {unlimited && <span style={{ fontSize: 10, marginLeft: 4 }}>∞</span>}
                    </div>
                  </div>
                  {!unlimited && (
                    <div style={{ height: 4, background: '#1a2a3a', borderRadius: 2 }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: barColor, borderRadius: 2, transition: 'width .4s' }} />
                    </div>
                  )}
                  {sub && <div style={{ fontSize: 10, color: '#7090a0', marginTop: 5 }}>{sub}</div>}
                  {danger && !unlimited && (
                    <div style={{ fontSize: 10, color: '#e05757', marginTop: 5, fontWeight: 600 }}>⚠ Limit almost reached</div>
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 16, padding: '10px 14px', background: '#060b10', border: '1px solid #1a2a3a', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 12, color: '#7090a0' }}>
              Need more capacity? Contact your administrator to upgrade your plan.
            </div>
            <div style={{ fontSize: 12, color: '#00e5a0', fontWeight: 700 }}>
              MYR {limits.price_monthly || 0}/mo
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
